import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  Shield, Truck, HeadphonesIcon, Clock, ChevronRight, Package, CheckCircle2, 
  Send, Home, Phone, MessageCircle, Gamepad2, Wrench, ArrowLeftRight, 
  Calculator, BookOpen, Award, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ContactHeader } from '../components/ui/whatsapp-button';
import { SchemaMarkup, SEOHead } from '../components/SEO/SchemaMarkup';

const languages = [
  { code: 'es', name: 'ES' },
  { code: 'en', name: 'EN' },
  { code: 'fr', name: 'FR' },
  { code: 'it', name: 'IT' },
  { code: 'de', name: 'DE' }
];

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useAuth();
  const [currentLang, setCurrentLang] = useState(i18n.language);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
    localStorage.setItem('language', lang);
  };

  const features = [
    { icon: Shield, title: t('features.warranty.title'), desc: t('features.warranty.desc') },
    { icon: Truck, title: t('features.shipping.title'), desc: t('features.shipping.desc') },
    { icon: HeadphonesIcon, title: t('features.support.title'), desc: t('features.support.desc') },
    { icon: Clock, title: t('features.fast.title'), desc: t('features.fast.desc') }
  ];

  const steps = [
    { icon: Package, title: t('howItWorks.step1.title'), desc: t('howItWorks.step1.desc') },
    { icon: Send, title: t('howItWorks.step2.title'), desc: t('howItWorks.step2.desc') },
    { icon: Clock, title: t('howItWorks.step3.title'), desc: t('howItWorks.step3.desc') },
    { icon: CheckCircle2, title: t('howItWorks.step4.title'), desc: t('howItWorks.step4.desc') }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* SEO Head with meta tags */}
      <SEOHead
        title="Arreglatelo - Reparación de Electrónica en España | Consolas, Móviles, Robots"
        description="Servicio técnico especializado en reparación de consolas, móviles, robots aspiradores y patinetes eléctricos. Envío gratis ida y vuelta. Garantía 3 meses."
        canonical="/"
      />
      
      {/* Schema Markup for Local Business */}
      <SchemaMarkup 
        type="LocalBusiness" 
        data={{
          name: "Arreglatelo - Servicio Técnico de Electrónica",
          phone: "+34-624-47-32-93",
          email: "soporte@arreglatelo.es",
          city: "Madrid",
          region: "Madrid",
          postalCode: "28001",
          rating: { value: "4.8", count: "256" }
        }}
      />
      
      {/* Schema Markup for Service */}
      <SchemaMarkup
        type="Service"
        data={{
          serviceType: "Electronics Repair",
          name: "Reparación de Electrónica",
          description: "Servicio técnico especializado en consolas gaming, móviles, robots aspiradores y patinetes eléctricos",
          services: [
            { name: "Reparación Nintendo Switch", description: "Joy-Con drift, pantalla, USB-C", price: "39", minPrice: "25", maxPrice: "149" },
            { name: "Reparación PlayStation 5", description: "HDMI, lector, ventilador", price: "59", minPrice: "39", maxPrice: "249" },
            { name: "Reparación iPhone", description: "Pantalla, batería, placa base", price: "69", minPrice: "49", maxPrice: "399" }
          ]
        }}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top bar with contact */}
          <div className="hidden sm:flex items-center justify-between py-2 text-xs border-b border-[#E5E7EB]/50">
            <div className="flex items-center gap-4">
              <a href="tel:+34624473293" className="flex items-center gap-1 text-[#52525B] hover:text-[#0047FF]">
                <Phone className="w-3 h-3" /> 624 47 32 93
              </a>
              <a href="https://wa.me/34624473293" className="flex items-center gap-1 text-[#25D366] hover:text-[#20BD5A]">
                <MessageCircle className="w-3 h-3" /> WhatsApp
              </a>
            </div>
            <div className="flex items-center gap-2 text-[#52525B]">
              <ShieldCheck className="w-3 h-3 text-[#0047FF]" />
              <span>Envío asegurado incluido</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-8 h-8 bg-[#0047FF] rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="font-heading font-bold text-xl tracking-tight">Arreglatelo</span>
            </Link>

            {/* Language Selector */}
            <div className="lang-selector" data-testid="language-selector">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`lang-btn ${currentLang === lang.code ? 'active' : ''}`}
                  data-testid={`lang-btn-${lang.code}`}
                >
                  {lang.name}
                </button>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <Link to={isAdmin ? '/admin' : '/dashboard'}>
                    <Button variant="ghost" data-testid="dashboard-nav-btn">
                      {t('nav.dashboard')}
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" data-testid="login-nav-btn">
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-[#0047FF] hover:bg-[#003BCC]" data-testid="register-nav-btn">
                      {t('nav.register')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white/90 to-[#F5F5F7]"></div>
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1549791084-5f78368b208b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc2MjY2MDY4fDA&ixlib=rb-4.1.0&q=85")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-overline mb-4 block">{t('hero.subtitle')}</span>
              <h1 className="text-hero font-heading mb-6" data-testid="hero-title">
                {t('hero.title')}
              </h1>
              <p className="text-lg text-[#52525B] mb-8 max-w-lg">
                {t('hero.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-[#0047FF] hover:bg-[#003BCC] text-lg px-8 py-6 h-auto"
                    data-testid="hero-cta-btn"
                  >
                    {t('hero.cta')}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="text-lg px-8 py-6 h-auto border-[#E5E7EB]"
                    data-testid="hero-login-btn"
                  >
                    {t('hero.login')}
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square max-w-lg mx-auto relative">
                <img
                  src="https://images.unsplash.com/photo-1698376621004-70ce754157d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMG1pbmltYWxpc3QlMjBzaGlwcGluZyUyMGJveCUyMG1vY2t1cHxlbnwwfHx8fDE3NzYyNjYwNzd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Shipping box"
                  className="rounded-2xl object-cover w-full h-full shadow-2xl"
                />
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#10B981]/10 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">7-30 días</p>
                    <p className="text-xs text-[#52525B]">Tiempo estimado</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-[#F5F5F7]" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-h2 font-heading mb-4">{t('features.title')}</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl border border-[#E5E7EB] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#0047FF]/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#0047FF]" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-[#52525B] text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-24 px-4" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-h2 font-heading mb-4">{t('howItWorks.title')}</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
                data-testid={`step-${index + 1}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-[#0047FF] rounded-xl flex items-center justify-center text-white">
                    <step.icon className="w-7 h-7" />
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block flex-1 h-0.5 bg-[#E5E7EB]"></div>
                  )}
                </div>
                <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-[#52525B] text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gaming Tools Section */}
      <section className="py-20 px-4 bg-white" data-testid="gaming-tools-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-h2 font-heading mb-4">Herramientas Gaming</h2>
            <p className="text-[#52525B] max-w-2xl mx-auto">
              Recursos gratuitos para sacar el máximo partido a tus consolas
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/guides">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-[#E60012]/10 to-[#E60012]/5 border border-[#E60012]/20 rounded-2xl p-6 hover:-translate-y-1 transition-all cursor-pointer"
              >
                <div className="w-14 h-14 bg-[#E60012] rounded-xl flex items-center justify-center mb-4">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Guías de Diagnóstico</h3>
                <p className="text-[#52525B] text-sm mb-4">
                  Soluciona problemas comunes antes de enviar tu producto a reparación
                </p>
                <span className="text-[#E60012] text-sm font-medium flex items-center gap-1">
                  Ver guías <ChevronRight className="w-4 h-4" />
                </span>
              </motion.div>
            </Link>

            <Link to="/tradein">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-[#107C10]/10 to-[#107C10]/5 border border-[#107C10]/20 rounded-2xl p-6 hover:-translate-y-1 transition-all cursor-pointer"
              >
                <div className="w-14 h-14 bg-[#107C10] rounded-xl flex items-center justify-center mb-4">
                  <Calculator className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Programa Trade-In</h3>
                <p className="text-[#52525B] text-sm mb-4">
                  Calcula el valor de tu consola y obtén crédito para una nueva
                </p>
                <span className="text-[#107C10] text-sm font-medium flex items-center gap-1">
                  Calcular valor <ChevronRight className="w-4 h-4" />
                </span>
              </motion.div>
            </Link>

            <Link to="/compare">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-[#003087]/10 to-[#003087]/5 border border-[#003087]/20 rounded-2xl p-6 hover:-translate-y-1 transition-all cursor-pointer"
              >
                <div className="w-14 h-14 bg-[#003087] rounded-xl flex items-center justify-center mb-4">
                  <ArrowLeftRight className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Comparador de Modelos</h3>
                <p className="text-[#52525B] text-sm mb-4">
                  Compara PS5 vs PS5 Slim, Switch vs OLED y más
                </p>
                <span className="text-[#003087] text-sm font-medium flex items-center gap-1">
                  Comparar <ChevronRight className="w-4 h-4" />
                </span>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Moved before footer */}
      <section className="py-24 px-4 bg-[#0A0A0A]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-h2 font-heading text-white mb-6">
              {t('hero.title')}
            </h2>
            <p className="text-[#A1A1AA] text-lg mb-8">
              {t('hero.description')}
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-[#E5FF00] hover:bg-[#d4ee00] text-[#0A0A0A] text-lg px-8 py-6 h-auto font-semibold"
                data-testid="cta-register-btn"
              >
                {t('hero.cta')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 px-4 bg-[#F5F5F7]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0047FF] mb-2">+5000</div>
              <p className="text-[#52525B] text-sm">Reparaciones completadas</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0047FF] mb-2">4.8/5</div>
              <p className="text-[#52525B] text-sm">Satisfacción cliente</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0047FF] mb-2">90 días</div>
              <p className="text-[#52525B] text-sm">Garantía en reparación</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#0047FF] mb-2">Gratis</div>
              <p className="text-[#52525B] text-sm">Envío ida y vuelta</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-[#E5E7EB] bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Logo and contact */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#0047FF] rounded-md flex items-center justify-center">
                  <span className="text-white font-bold text-lg">A</span>
                </div>
                <span className="font-heading font-bold text-xl">Arreglatelo</span>
              </div>
              <div className="space-y-2 text-sm text-[#52525B]">
                <a href="tel:+34624473293" className="flex items-center gap-2 hover:text-[#0047FF]">
                  <Phone className="w-4 h-4" /> 624 47 32 93
                </a>
                <a href="https://wa.me/34624473293" className="flex items-center gap-2 hover:text-[#25D366]">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                <p className="flex items-center gap-2 text-[#EF4444]">
                  <Phone className="w-4 h-4" /> Emergencias: 642 67 69 10
                </p>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-semibold mb-4">Servicios</h4>
              <ul className="space-y-2 text-sm text-[#52525B]">
                <li><Link to="/warranty/new" className="hover:text-[#0047FF]">Solicitar Garantía</Link></li>
                <li><Link to="/invoice/new" className="hover:text-[#0047FF]">Solicitar Factura</Link></li>
                <li><Link to="/tradein" className="hover:text-[#0047FF]">Trade-In</Link></li>
                <li><Link to="/guides" className="hover:text-[#0047FF]">Guías DIY</Link></li>
              </ul>
            </div>

            {/* Consoles */}
            <div>
              <h4 className="font-semibold mb-4">Consolas</h4>
              <ul className="space-y-2 text-sm text-[#52525B]">
                <li>Nintendo Switch / Switch 2</li>
                <li>PlayStation 5 / PS4 / PS3 / PS2</li>
                <li>Xbox Series X|S / Xbox One</li>
                <li>Mandos y accesorios</li>
              </ul>
            </div>

            {/* WhatsApp Groups */}
            <div>
              <h4 className="font-semibold mb-4">Únete a la comunidad</h4>
              <div className="space-y-2">
                <a 
                  href="https://chat.whatsapp.com/promociones" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm bg-[#25D366] text-white px-4 py-2 rounded-lg hover:bg-[#20BD5A] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> Grupo Promociones
                </a>
                <a 
                  href="https://chat.whatsapp.com/descuentos" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm bg-[#25D366] text-white px-4 py-2 rounded-lg hover:bg-[#20BD5A] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> Códigos Descuento
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-6 pt-8 border-t border-[#E5E7EB]">
            <div className="flex items-center gap-6 text-sm text-[#52525B]">
              <a href="#" className="hover:text-[#0A0A0A] transition-colors">{t('footer.privacy')}</a>
              <a href="#" className="hover:text-[#0A0A0A] transition-colors">{t('footer.terms')}</a>
              <a href="#" className="hover:text-[#0A0A0A] transition-colors">{t('footer.contact')}</a>
            </div>
            <p className="text-sm text-[#A1A1AA]">
              © {new Date().getFullYear()} Arreglatelo. {t('footer.rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
